import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  Building, 
  Users, 
  Shield, 
  FileCheck, 
  AlertTriangle,
  Plus,
  RefreshCw,
  Loader2,
  Crown,
  CheckCircle,
  Clock,
  Database,
  ShieldAlert
} from 'lucide-react';
import api from '../lib/api';

export default function AdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newOrg, setNewOrg] = useState({
    name: '',
    admin_email: '',
    admin_name: '',
    admin_password: '',
    tier: 'trial'
  });

  // Check admin access
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    const fetchData = async () => {
      if (!isAdmin) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const [statsRes, orgsRes] = await Promise.all([
          api.get('/admin/stats'),
          api.get('/admin/organizations')
        ]);
        setStats(statsRes.data);
        setOrganizations(orgsRes.data);
      } catch (error) {
        toast.error('Failed to load admin data.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [isAdmin]);

  const refreshData = async () => {
    setLoading(true);
    try {
      const [statsRes, orgsRes] = await Promise.all([
        api.get('/api/admin/stats'),
        api.get('/api/admin/organizations')
      ]);
      setStats(statsRes.data);
      setOrganizations(orgsRes.data);
    } catch (error) {
      toast.error('Failed to load admin data.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrg = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const params = new URLSearchParams({
        name: newOrg.name,
        admin_email: newOrg.admin_email,
        admin_name: newOrg.admin_name,
        admin_password: newOrg.admin_password,
        tier: newOrg.tier
      });
      const response = await api.post(`/admin/create-organization?${params}`);
      toast.success('Organization created successfully!');
      toast.info(`Credentials: ${response.data.credentials.email} / ${response.data.credentials.password}`);
      setShowCreateForm(false);
      setNewOrg({ name: '', admin_email: '', admin_name: '', admin_password: '', tier: 'trial' });
      refreshData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create organization');
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateTier = async (orgId, newTier) => {
    try {
      await api.put(`/admin/organization/${orgId}/subscription?tier=${newTier}`);
      toast.success('Subscription updated');
      refreshData();
    } catch (error) {
      toast.error('Failed to update subscription');
    }
  };

  const getTierBadge = (tier) => {
    const styles = {
      trial: 'bg-blue-100 text-blue-700',
      starter: 'bg-slate-100 text-slate-700',
      professional: 'bg-purple-100 text-purple-700',
      enterprise: 'bg-amber-100 text-amber-700'
    };
    return <Badge className={styles[tier] || styles.trial}>{tier}</Badge>;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </DashboardLayout>
    );
  }

  // Access denied for non-admin users
  if (!isAdmin) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center h-64 text-center" data-testid="admin-access-denied">
          <ShieldAlert className="w-16 h-16 text-red-400 mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Access Denied</h1>
          <p className="text-slate-500 mb-4">You do not have permission to access the Admin Panel.</p>
          <p className="text-sm text-slate-400">This area is restricted to administrators only.</p>
          <Button 
            variant="outline" 
            className="mt-6"
            onClick={() => navigate('/dashboard')}
            data-testid="back-to-dashboard-btn"
          >
            Back to Dashboard
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Admin Panel</h1>
            <p className="text-slate-500 mt-1">Manage organizations and platform settings</p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={refreshData} data-testid="admin-refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={() => setShowCreateForm(!showCreateForm)} data-testid="admin-new-org-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Organization
            </Button>
          </div>
        </div>

        {/* Platform Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <Building className="w-8 h-8 text-blue-600" />
                  <div>
                    <p className="text-2xl font-bold">{stats.totals.organizations}</p>
                    <p className="text-xs text-slate-500">Organizations</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <Users className="w-8 h-8 text-green-600" />
                  <div>
                    <p className="text-2xl font-bold">{stats.totals.users}</p>
                    <p className="text-xs text-slate-500">Users</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <Shield className="w-8 h-8 text-purple-600" />
                  <div>
                    <p className="text-2xl font-bold">{stats.totals.controls}</p>
                    <p className="text-xs text-slate-500">Controls</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <FileCheck className="w-8 h-8 text-amber-600" />
                  <div>
                    <p className="text-2xl font-bold">{stats.totals.evidence}</p>
                    <p className="text-xs text-slate-500">Evidence</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-8 h-8 text-red-600" />
                  <div>
                    <p className="text-2xl font-bold">{stats.totals.risks}</p>
                    <p className="text-xs text-slate-500">Risks</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <Database className="w-8 h-8 text-slate-600" />
                  <div>
                    <p className="text-2xl font-bold">{stats.totals.policies}</p>
                    <p className="text-xs text-slate-500">Policies</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <Crown className="w-8 h-8 text-indigo-600" />
                  <div>
                    <p className="text-2xl font-bold">{stats.totals.frameworks}</p>
                    <p className="text-xs text-slate-500">Frameworks</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Subscription Breakdown */}
        {stats && (
          <Card>
            <CardHeader>
              <CardTitle>Subscription Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-8">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                  <span className="text-sm">Trial: {stats.subscriptions.trial}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-slate-500"></div>
                  <span className="text-sm">Starter: {stats.subscriptions.starter}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                  <span className="text-sm">Professional: {stats.subscriptions.professional}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                  <span className="text-sm">Enterprise: {stats.subscriptions.enterprise}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Create Organization Form */}
        {showCreateForm && (
          <Card>
            <CardHeader>
              <CardTitle>Create New Organization</CardTitle>
              <CardDescription>Set up a new customer account</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateOrg} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700">Organization Name</label>
                    <Input
                      value={newOrg.name}
                      onChange={(e) => setNewOrg({ ...newOrg, name: e.target.value })}
                      placeholder="Neotecra Inc."
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Subscription Tier</label>
                    <select
                      value={newOrg.tier}
                      onChange={(e) => setNewOrg({ ...newOrg, tier: e.target.value })}
                      className="w-full h-10 px-3 rounded-md border border-slate-200"
                    >
                      <option value="trial">Trial (14 days)</option>
                      <option value="starter">Starter ($299/mo)</option>
                      <option value="professional">Professional ($799/mo)</option>
                      <option value="enterprise">Enterprise ($1999/mo)</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Admin Name</label>
                    <Input
                      value={newOrg.admin_name}
                      onChange={(e) => setNewOrg({ ...newOrg, admin_name: e.target.value })}
                      placeholder="John Smith"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Admin Email</label>
                    <Input
                      type="email"
                      value={newOrg.admin_email}
                      onChange={(e) => setNewOrg({ ...newOrg, admin_email: e.target.value })}
                      placeholder="john@neotecra.com"
                      required
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-sm font-medium text-slate-700">Admin Password</label>
                    <Input
                      type="text"
                      value={newOrg.admin_password}
                      onChange={(e) => setNewOrg({ ...newOrg, admin_password: e.target.value })}
                      placeholder="Generate a secure password"
                      required
                    />
                    <p className="text-xs text-slate-500 mt-1">This will be shared with the customer</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Button type="submit" disabled={creating}>
                    {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                    Create Organization
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowCreateForm(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Organizations List */}
        <Card>
          <CardHeader>
            <CardTitle>Organizations ({organizations.length})</CardTitle>
            <CardDescription>All customer accounts</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {organizations.map((org) => (
                <div 
                  key={org.id} 
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                      <Building className="w-5 h-5 text-slate-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-slate-900">{org.name}</h3>
                      <p className="text-sm text-slate-500">
                        {org.user_count} user(s) • Created {new Date(org.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      {org.onboarding_completed ? (
                        <Badge variant="outline" className="text-green-600 border-green-200">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Onboarded
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-amber-600 border-amber-200">
                          <Clock className="w-3 h-3 mr-1" />
                          Pending
                        </Badge>
                      )}
                    </div>
                    {getTierBadge(org.subscription?.tier || 'trial')}
                    <select
                      value={org.subscription?.tier || 'trial'}
                      onChange={(e) => handleUpdateTier(org.id, e.target.value)}
                      className="text-sm border rounded px-2 py-1"
                    >
                      <option value="trial">Trial</option>
                      <option value="starter">Starter</option>
                      <option value="professional">Professional</option>
                      <option value="enterprise">Enterprise</option>
                    </select>
                  </div>
                </div>
              ))}
              {organizations.length === 0 && (
                <p className="text-center text-slate-500 py-8">No organizations yet</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
